/**
 * Etapa 7 - Acesso e revisao: dono da conta + conferencia de tudo (inclusive
 * a politica de estorno e o generico, quando marcado).
 *
 * A revisao mostra cada etapa com um "Editar" que volta para ela (os passos
 * ja concluidos sao clicaveis no Stepper tambem). Segredos de webhook NAO
 * aparecem aqui: so "informado" ou "faltando". O convite por e-mail e uma
 * caixa marcada por padrao com a nota de que, na previa, nada e enviado.
 */

import type { ReactNode } from 'react';
import { Badge, Button, Checkbox, CompanyLogo, Field, Fieldset, Input, SectionCard } from '../../../../components/ui';
import { VALOR_AUSENTE, formatBRLFromCents, formatCount, formatPercentFromBps, maskPhoneDisplay } from '../../../../lib/format';
import { ETAPAS, mascararCnpj, telefoneE164 } from '../../assistente';
import { iniciaisDe } from '../../identidade';
import { FUNIL_INFO, MODELO_COBRANCA, POLITICA_ESTORNO, POLITICA_PADRAO, PROVEDOR_INFO, TONE_NAME } from '../../vocabulario';
import { erroDe, type EtapaProps } from './props';

function Linha({ rotulo, children }: { readonly rotulo: string; readonly children: ReactNode }): JSX.Element {
  return (
    <div className="grid gap-0.5 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-xs font-medium text-ink-subtle">{rotulo}</dt>
      <dd className="min-w-0 text-sm text-ink">{children}</dd>
    </div>
  );
}

function Bloco({ etapa, onGoTo, children }: { readonly etapa: number; readonly onGoTo: (etapa: number) => void; readonly children: ReactNode }): JSX.Element {
  const titulo = ETAPAS[etapa]?.label ?? `Etapa ${etapa + 1}`;
  return (
    <section aria-label={titulo} className="flex flex-col gap-3 border-b border-dashed border-line pb-4 last:border-0 last:pb-0">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">
          <span className="mr-2 inline-grid h-5 w-5 place-items-center rounded-full bg-ink/5 text-[10px] font-semibold text-ink-subtle">{etapa + 1}</span>
          {titulo}
        </h3>
        <Button variant="ghost" size="sm" icon="editar" onClick={() => onGoTo(etapa)}>
          Editar<span className="sr-only"> {titulo}</span>
        </Button>
      </div>
      <dl className="flex flex-col gap-2">{children}</dl>
    </section>
  );
}

export function EtapaAcesso(props: EtapaProps & { readonly onGoTo: (etapa: number) => void }): JSX.Element {
  const { form, onChange, onGoTo } = props;
  const nome = form.nome.trim() === '' ? 'Nova empresa' : form.nome.trim();
  const telefone = telefoneE164(form.contatoTelefone);
  const modelo = MODELO_COBRANCA[form.billingModel];
  const preview = { name: nome, logoInitials: iniciaisDe(nome), logoTone: form.logoTone, logoUrl: form.logoDataUrl };

  return (
    <div className="flex flex-col gap-4">
      <Fieldset legend="Dono da conta" description="Quem entra no painel com papel de dono: vê tudo, altera configurações e convida a equipe.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome" required error={erroDe(props, 'donoNome')}>
            {({ id, describedBy, invalid }) => <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} value={form.donoNome} onChange={(e) => onChange({ donoNome: e.target.value })} autoComplete="name" />}
          </Field>
          <Field label="E-mail de acesso" required error={erroDe(props, 'donoEmail')}>
            {({ id, describedBy, invalid }) => (
              <Input id={id} type="email" inputMode="email" aria-describedby={describedBy} aria-invalid={invalid} value={form.donoEmail} onChange={(e) => onChange({ donoEmail: e.target.value })} autoComplete="email" />
            )}
          </Field>
        </div>
        <Checkbox checked={form.enviarConvite} onChange={(marcado) => onChange({ enviarConvite: marcado })} label="Enviar convite por e-mail" description="Na prévia nada é enviado. Na versão real, o dono recebe um e-mail para criar a senha e entrar no painel." />
      </Fieldset>

      <SectionCard id="revisao" title="Revisão" icon="check" description="Confira antes de criar. Qualquer bloco pode ser editado.">
        <div className="flex flex-col gap-4">
          <Bloco etapa={0} onGoTo={onGoTo}>
            <Linha rotulo="Empresa">
              {nome} <span className="text-ink-muted">· {form.segmento.trim() === '' ? VALOR_AUSENTE : form.segmento}</span>
            </Linha>
            <Linha rotulo="CNPJ">{form.cnpj === '' ? VALOR_AUSENTE : mascararCnpj(form.cnpj)}</Linha>
            <Linha rotulo="Site">{form.site.trim() === '' ? VALOR_AUSENTE : form.site}</Linha>
            <Linha rotulo="Contato">
              {form.contatoNome.trim() === '' ? VALOR_AUSENTE : form.contatoNome} <span className="text-ink-muted">· {form.contatoEmail.trim() === '' ? VALOR_AUSENTE : form.contatoEmail}</span>
              {telefone === null ? null : <span className="text-ink-muted"> · {maskPhoneDisplay(telefone)}</span>}
            </Linha>
          </Bloco>

          <Bloco etapa={1} onGoTo={onGoTo}>
            <Linha rotulo="Logo e cor">
              <span className="flex items-center gap-2">
                <CompanyLogo company={preview} size="sm" />
                {form.logoDataUrl === null ? 'Iniciais' : 'Imagem enviada'} · tom {TONE_NAME[form.logoTone]}
              </span>
            </Linha>
          </Bloco>

          <Bloco etapa={2} onGoTo={onGoTo}>
            <Linha rotulo="Comissão">{formatPercentFromBps(form.feeBps)} sobre o valor recuperado</Linha>
            <Linha rotulo="Cobrança">
              <span className="flex flex-wrap items-center gap-2">
                <Badge tone={modelo.tone}>{modelo.label}</Badge>
                <span className="text-xs text-ink-subtle">decisão pendente do dono</span>
              </span>
            </Linha>
            <Linha rotulo="Estornos">
              <span className="flex flex-wrap items-center gap-2">
                <Badge tone={POLITICA_ESTORNO[form.refundPolicy].tone}>{POLITICA_ESTORNO[form.refundPolicy].curto}</Badge>
                <span className="text-xs text-ink-subtle">{form.refundPolicy === POLITICA_PADRAO ? 'padrão decidido: reembolso não reverte, chargeback reverte' : POLITICA_ESTORNO[form.refundPolicy].explica}</span>
              </span>
            </Linha>
          </Bloco>

          <Bloco etapa={3} onGoTo={onGoTo}>
            <Linha rotulo={`Ofertas (${formatCount(form.ofertas.length)})`}>
              {form.ofertas.length === 0 ? (
                VALOR_AUSENTE
              ) : (
                <ul className="flex flex-col gap-1">
                  {form.ofertas.map((o) => (
                    <li key={o.id} className="flex flex-wrap items-center gap-2">
                      <span>{o.nome}</span>
                      <span className="tabular-nums text-ink-muted">{formatBRLFromCents(o.precoCents)}</span>
                      <Badge tone={o.recorrente ? 'info' : 'neutral'}>{o.recorrente ? 'Recorrente' : 'Compra única'}</Badge>
                      <span className="text-xs text-ink-subtle">{PROVEDOR_INFO[o.provider].curto}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Linha>
          </Bloco>

          <Bloco etapa={4} onGoTo={onGoTo}>
            <Linha rotulo="Provedores">
              {form.provedores.length === 0 ? (
                VALOR_AUSENTE
              ) : (
                <span className="flex flex-wrap gap-1.5">
                  {form.provedores.map((p) => (
                    <Badge key={p} tone="neutral" icon="link">
                      {PROVEDOR_INFO[p].curto}
                    </Badge>
                  ))}
                </span>
              )}
            </Linha>
            <Linha rotulo="Segredos">
              <span className="flex flex-wrap gap-1.5">
                {form.provedores.map((p) =>
                  form.segredos[p].trim() === '' ? (
                    <Badge key={p} tone="warning" icon="chave">
                      {PROVEDOR_INFO[p].curto}: faltando
                    </Badge>
                  ) : (
                    <Badge key={p} tone="good" icon="chave">
                      {PROVEDOR_INFO[p].curto}: informado
                    </Badge>
                  ),
                )}
                {form.provedores.length === 0 ? VALOR_AUSENTE : null}
              </span>
            </Linha>
            <Linha rotulo="WhatsApp">{form.evolutionInstancia.trim() === '' ? VALOR_AUSENTE : <code className="font-mono text-xs">{form.evolutionInstancia}</code>}</Linha>
            <Linha rotulo="Voz">{form.vapiAssistente.trim() === '' ? VALOR_AUSENTE : form.vapiAssistente}</Linha>
          </Bloco>

          <Bloco etapa={5} onGoTo={onGoTo}>
            <Linha rotulo="Funis">
              {form.funis.length === 0 ? (
                VALOR_AUSENTE
              ) : (
                <span className="flex flex-wrap gap-1.5">
                  {form.funis.map((f) => (
                    <Badge key={f} tone="good" dot>
                      {FUNIL_INFO[f].label}
                    </Badge>
                  ))}
                </span>
              )}
            </Linha>
          </Bloco>

          <Bloco etapa={6} onGoTo={onGoTo}>
            <Linha rotulo="Dono">
              {form.donoNome.trim() === '' ? VALOR_AUSENTE : form.donoNome} <span className="text-ink-muted">· {form.donoEmail.trim() === '' ? VALOR_AUSENTE : form.donoEmail}</span>
            </Linha>
            <Linha rotulo="Convite">{form.enviarConvite ? 'Enviar por e-mail (na prévia, nada é enviado)' : 'Não enviar agora'}</Linha>
          </Bloco>
        </div>
      </SectionCard>
    </div>
  );
}
